import { memo, useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { RotaryKnob, RockerSwitch, LedIndicator, PilotLamp } from "./SignalControls";

// Modbus tables that are inherently binary (0/1) - get the switch/lamp
// graphic, same as a physical digital port. Holding/input registers get the
// knob/gauge graphic instead, same as a physical analog port. This is a
// deliberate simplification for now: a register's real engineering range
// (from its codec's scale/bias) isn't accounted for by the knob's fixed
// 0-100% sweep - fine for a first pass, revisited once multi-byte/variable-
// length registers need their own real range instead of the raw 0-1023
// convention every other writable signal here already uses.
const BINARY_TABLES = ["coil", "discrete_input"];
const ANALOG_TABLES = ["holding_register", "input_register"];

// Per-binding status a device reports on /status/{hw-id} (see
// 00117-starek-324-nwm's signals/wire.h ST_*, mirrored by the backend's
// Device.WIRE.STATUS) - index is the wire code.
const STATUS_LABELS = [
    "OK",
    "No such endpoint",
    "Protocol not supported here",
    "Conflicts with another signal on this bus",
    "Codec not supported",
    "Driver failed",
    "Invalid configuration",
    "Too many signals on this bus",
    "Name conflict",
    "Comms failing",
    "Not writable",
];

// The interactive/readout widget (slider, toggle, or latest-value display)
// for the current signal - kept as its own component so the LiveView modal
// can lay it out beside the graph (right column) instead of stacking it
// above/below. See DeviceGraph.jsx.
export const DeviceMonitorControlWidget = memo(
    ({
        signalTypeInfo,
        isExpanded,
        initialValue,
        onValueChange,
        inverted,
        isInterpretation,
        access,
        status,
        bindingProtocol,
        bindingTable,
        signalColor,
    }) => {
        // Convert raw (wire) value to a percentage - "Inverted" is a wiring/
        // logic fact (e.g. a NC relay reads 1023 for physically OFF), not a
        // change to what the device puts on the wire, so it's applied here
        // (the interpreted %/ON-OFF reading) and undone in percentageToRaw
        // (so "Raw" always round-trips back to the true wire value, same
        // convention as the historical graph - see DeviceGraph.jsx).
        const rawToPercentage = (rawValue) => {
            const raw = rawValue || 0;
            const effective = inverted ? 1023 - raw : raw;
            return (effective / 1023) * 100;
        };

        // Convert percentage (0-100) to raw (wire) value
        const percentageToRaw = (percentage) => {
            const effective = Math.round((percentage / 100) * 1023);
            return inverted ? 1023 - effective : effective;
        };

        const [rangeValue, setRangeValue] = useState(
            rawToPercentage(initialValue)
        );
        const [toggleValue, setToggleValue] = useState(
            rawToPercentage(initialValue)
        );
        // An interpretation's write target is a real engineering value (the
        // codec's scale/bias already applied - see the firmware's
        // set-signal handler), never a 0-1023 raw percentage the way a
        // direct GPIO/PWM write is, so it gets a plain number field instead
        // of the slider above.
        const [engineeringValue, setEngineeringValue] = useState(
            initialValue ?? 0
        );
        const sliderUpdateTimeout = useRef(null);

        // Update local state when initialValue changes
        useEffect(() => {
            const percentage = rawToPercentage(initialValue);
            setRangeValue(percentage);
            setToggleValue(percentage);
            setEngineeringValue(initialValue ?? 0);
        }, [initialValue]);

        // RotaryKnob reports a rounded 0-100 continuously while dragging -
        // update the visual position immediately, but debounce the actual
        // network call the same way the plain slider always has, so a drag
        // doesn't flood set-signal commands.
        const handleKnobChange = (percentageVal) => {
            setRangeValue(percentageVal);
            if (sliderUpdateTimeout.current) {
                clearTimeout(sliderUpdateTimeout.current);
            }
            sliderUpdateTimeout.current = setTimeout(() => {
                onValueChange(percentageToRaw(percentageVal));
            }, 500);
        };

        // A click is one discrete event, not a drag - send immediately,
        // same as the digital slider above always has.
        const handleSwitchToggle = () => {
            const next = toggleValue >= 50 ? 0 : 100;
            setToggleValue(next);
            onValueChange(percentageToRaw(next));
        };

        const handleEngineeringValueChange = (e) => {
            const raw = e.target.value;
            setEngineeringValue(raw);
            if (raw === "" || Number.isNaN(Number(raw))) return;

            if (sliderUpdateTimeout.current) {
                clearTimeout(sliderUpdateTimeout.current);
            }
            sliderUpdateTimeout.current = setTimeout(() => {
                onValueChange(Number(raw));
            }, 500);
        };

        // Not OK means the device rejected or can't currently service this
        // binding (see signals/wire.h's ST_* codes) - shown regardless of
        // whether the widget below ends up interactive or read-only.
        const statusBadge =
            status !== undefined && status !== 0 ? (
                <div className="tw-text-xs tw-text-red-600 tw-italic tw-mt-2">
                    {STATUS_LABELS[status] || `Status ${status}`}
                </div>
            ) : null;

        // A physical port or a "direct" interpretation (which just rescales
        // its source's own raw value - see DeviceGraph.jsx's sourceType)
        // decodes analog/digital from real type bits, purely to pick which
        // GRAPHIC (knob vs. switch) fits the value's shape; any other
        // binding (i2c/modbus/raw_frame) has no such bits, so its shape
        // comes from the Modbus table instead (BINARY_TABLES/ANALOG_TABLES).
        const isDirectlyTyped = !isInterpretation || bindingProtocol === "direct";
        const isAnalogKind = isDirectlyTyped
            ? signalTypeInfo.analog && (signalTypeInfo.output || signalTypeInfo.input)
            : ANALOG_TABLES.includes(bindingTable);
        const isDigitalKind = isDirectlyTyped
            ? signalTypeInfo.digital && (signalTypeInfo.output || signalTypeInfo.input)
            : BINARY_TABLES.includes(bindingTable);
        // Whether it's interactive is a DIFFERENT question from its shape,
        // and always comes from the one server-derived `access.write` (see
        // DeviceGraph.jsx's comment: "never recomputed here, so the UI and
        // backend never disagree") - never re-derived from signalTypeInfo,
        // even for a physical row (where access.write is computed from that
        // exact same type bit anyway, so this changes nothing for those,
        // only makes a "direct" interpretation's widget agree with its own
        // Read-only/Read-Write chip instead of re-deciding independently).
        const canControl = !!access?.write;
        const rawAnalogValue = percentageToRaw(rangeValue);
        const rawToggleValue = percentageToRaw(toggleValue);

        if (!isExpanded) return null;

        if (isAnalogKind) {
            return (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full tw-gap-1">
                    <RotaryKnob
                        value={rangeValue}
                        onChange={handleKnobChange}
                        interactive={canControl}
                        color={signalColor}
                    />
                    <div className="tw-text-sm tw-text-gray-600">
                        {rangeValue.toFixed(1)}%
                    </div>
                    <div className="tw-text-xs tw-text-gray-500">
                        Raw: {rawAnalogValue}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-italic">
                        {canControl
                            ? "Drag to set the value"
                            : "Sense-only signal - showing latest reading"}
                    </div>
                    {statusBadge}
                </div>
            );
        }

        if (isDigitalKind) {
            return (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full tw-gap-2">
                    {canControl ? (
                        <>
                            <RockerSwitch
                                on={toggleValue >= 50}
                                onToggle={handleSwitchToggle}
                                interactive
                                color={signalColor}
                            />
                            <LedIndicator on={toggleValue >= 50} color={signalColor} />
                        </>
                    ) : (
                        <PilotLamp on={toggleValue >= 50} color={signalColor} />
                    )}
                    <div
                        className={`tw-text-sm tw-font-medium ${
                            toggleValue >= 50 ? "tw-text-green-600" : "tw-text-gray-500"
                        }`}
                    >
                        {toggleValue >= 50 ? "ON" : "OFF"}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500">
                        Raw: {rawToggleValue}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-italic">
                        {canControl ? "Click to toggle" : "Sense-only signal - showing latest reading"}
                    </div>
                    {statusBadge}
                </div>
            );
        }

        // Whatever's left once isAnalogKind/isDigitalKind above didn't match:
        // an I2C register or a raw-frame listener, neither of which is
        // inherently binary or has a real value range to put on a knob -
        // deliberately punted to a plain numeric field/readout for now (see
        // BINARY_TABLES/ANALOG_TABLES's comment) rather than guessing a
        // sweep range. Access is still the server-derived `access.write`
        // (utils/signalWire.ts's deriveAccess), never re-inferred here.
        if (isInterpretation) {
            if (access?.write) {
                return (
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full">
                        <input
                            type="number"
                            value={engineeringValue}
                            onChange={handleEngineeringValueChange}
                            className="tw-w-32 tw-text-center tw-text-2xl tw-font-semibold tw-border tw-rounded tw-py-1"
                        />
                        <div className="tw-text-xs tw-text-gray-500 tw-italic tw-mt-2">
                            Enter a value and it's sent after a short pause
                        </div>
                        {statusBadge}
                    </div>
                );
            }
            return (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full">
                    <div className="tw-text-3xl tw-font-semibold tw-text-gray-800">
                        {initialValue ?? "—"}
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-italic tw-mt-1">
                        {access?.write === false
                            ? "Read-only signal"
                            : "Showing latest reading"}
                    </div>
                    {statusBadge}
                </div>
            );
        }

        return null;
    }
);

DeviceMonitorControlWidget.displayName = "DeviceMonitorControlWidget";

// Header row: prev/next nav, signal type/inverted/auxiliary badges, signal
// count. The control widget itself is rendered separately (see
// DeviceMonitorControlWidget above) so DeviceGraph.jsx can place it beside
// the graph instead of stacking it above/below.
// Modbus table id -> display label, and a fixed lo-to-hi hex/decimal
// formatter each chip below uses so an address always reads the same way
// regardless of which signal it's on.
const TABLE_LABELS = {
    coil: "Coil",
    discrete_input: "Discrete Input",
    holding_register: "Holding Register",
    input_register: "Input Register",
};
const hex = (n) => `0x${n.toString(16).padStart(2, "0").toUpperCase()}`;

const DeviceMonitorControl = memo(
    ({
        currentSignalKey,
        signalTypeInfo,
        isExpanded,
        currentSignalIndex,
        totalSignals,
        onPrevSignal,
        onNextSignal,
        isInverted,
        isAuxiliary,
        status,
        access,
        bindingProtocol,
        bindingTable,
        bindingUnit,
        bindingAddress,
    }) => {
        // A physical port or a "direct" interpretation decodes its shape/
        // access from real type bits (signalTypeInfo, already resolved for
        // "direct" against its SOURCE's type - see DeviceGraph.jsx); any
        // other binding has none, so the same basic chips are derived from
        // the binding itself instead, using the identical Sense-is-read/
        // Actuate-is-write rule (a coil or holding register is exactly as
        // much "Actuate" as a physical output line).
        const isDirectlyTyped = !bindingProtocol || bindingProtocol === "direct";
        const isDiscrete = isDirectlyTyped ? signalTypeInfo.digital : BINARY_TABLES.includes(bindingTable);
        const isProportional = isDirectlyTyped ? signalTypeInfo.analog : ANALOG_TABLES.includes(bindingTable);
        const isSense = isDirectlyTyped ? signalTypeInfo.input : !access?.write;
        const isActuate = isDirectlyTyped ? signalTypeInfo.output : !!access?.write;
        const busLabel = isDirectlyTyped
            ? null // GPIO/UART/I2C/Virtual chips below already cover this case
            : bindingProtocol === "i2c"
              ? "I2C Bus"
              : "UART Bus"; // modbus_rtu and raw_frame both ride /uart

        // The addressing that makes this binding find its own byte(s) on the
        // bus - meaningless for a physical port or a "direct" signal (there's
        // nothing to address beyond the endpoint itself), so nothing renders
        // on the right for those.
        const addressChip =
            bindingProtocol === "i2c" ? (
                <span className="tw-px-3 tw-py-1 tw-bg-cyan-100 tw-text-cyan-700 tw-text-xs tw-font-semibold tw-rounded-full">
                    <i className="fa fa-hashtag tw-me-1" />
                    {`addr ${hex(bindingUnit ?? 0)} · reg ${bindingAddress ?? 0}`}
                </span>
            ) : bindingProtocol === "modbus_rtu" ? (
                <span className="tw-px-3 tw-py-1 tw-bg-cyan-100 tw-text-cyan-700 tw-text-xs tw-font-semibold tw-rounded-full">
                    <i className="fa fa-hashtag tw-me-1" />
                    {`${TABLE_LABELS[bindingTable] || bindingTable} · unit ${bindingUnit ?? 0} · addr ${bindingAddress ?? 0}`}
                </span>
            ) : bindingProtocol === "raw_frame" ? (
                <span className="tw-px-3 tw-py-1 tw-bg-cyan-100 tw-text-cyan-700 tw-text-xs tw-font-semibold tw-rounded-full">
                    <i className="fa fa-hashtag tw-me-1" />
                    Raw frame listener
                </span>
            ) : null;

        return (
            <div className={`tw-border-b ${isExpanded ? "tw-px-6 tw-py-4" : "tw-p-1"}`}>
                <div className="tw-flex tw-items-center tw-justify-between">
                    <button
                        className={`tw-rounded-full tw-text-white tw-transition-colors ${
                            isExpanded ? "tw-p-2" : "tw-p-1"
                        } ${
                            currentSignalIndex === 0
                                ? "tw-bg-gray-300 tw-cursor-not-allowed"
                                : "tw-bg-blue-500 hover:tw-bg-blue-600"
                        }`}
                        onClick={onPrevSignal}
                        disabled={currentSignalIndex === 0}
                    >
                        <ChevronLeft
                            className={`${
                                isExpanded ? "tw-h-6 tw-w-6" : "tw-h-4 tw-w-4"
                            }`}
                        />
                    </button>

                    <div
                        className={`tw-flex-1 tw-flex ${
                            isExpanded
                                ? "tw-flex-row tw-items-center tw-justify-between tw-px-6"
                                : "tw-flex-col tw-items-center"
                        }`}
                    >
                        {/* Every fact about the signal, as chips - the signal
                            name itself lives in the modal's own title bar
                            (see Devices.jsx's onCurrentSignalChange), not
                            duplicated here. Split left/right: left is what
                            the signal fundamentally IS (bus, shape, sense/
                            actuate, inverted/auxiliary) - the same handful
                            of facts every signal has. Right is what makes
                            THIS one findable on its bus (I2C address, Modbus
                            table/unit/register) - only a binding with real
                            addressing has any of these, and a physical or
                            "direct" signal has nothing to put there at all,
                            same as no separate R/W chip: sense-is-read vs.
                            actuate-is-write is already the Sense/Actuate
                            chip, and the widget's own graphic (switch/knob
                            vs. lamp/gauge) says the same thing again. */}
                        <div className="tw-flex tw-flex-col tw-items-start tw-justify-center">
                            {!isExpanded && (
                                <div className="tw-font-bold tw-text-gray-800 tw-text-sm">
                                    {currentSignalKey}
                                </div>
                            )}
                            {isExpanded && (
                                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-4 tw-gap-y-2 tw-w-full">
                                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                                        {isProportional && (
                                            <span className="tw-px-3 tw-py-1 tw-bg-purple-100 tw-text-purple-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                                <i className="fa fa-chart-line tw-me-1" />
                                                Proportional
                                            </span>
                                        )}
                                        {isDiscrete && (
                                            <span className="tw-px-3 tw-py-1 tw-bg-green-100 tw-text-green-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                                <i className="fa fa-wave-square tw-me-1" />
                                                Discrete
                                            </span>
                                        )}
                                        {isSense && (
                                            <span className="tw-px-3 tw-py-1 tw-bg-blue-100 tw-text-blue-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                                <i className="fa fa-eye tw-me-1" />
                                                Sense
                                            </span>
                                        )}
                                        {isActuate && (
                                            <span className="tw-px-3 tw-py-1 tw-bg-orange-100 tw-text-orange-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                                <i className="fa fa-bolt tw-me-1" />
                                                Actuate
                                            </span>
                                        )}
                                        {signalTypeInfo.status && (
                                            <span className="tw-px-3 tw-py-1 tw-bg-yellow-100 tw-text-yellow-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                                <i className="fa fa-info-circle tw-me-1" />
                                                Status
                                            </span>
                                        )}
                                        {signalTypeInfo.control && (
                                            <span className="tw-px-3 tw-py-1 tw-bg-red-100 tw-text-red-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                                <i className="fa fa-sliders-h tw-me-1" />
                                                Control
                                            </span>
                                        )}
                                        {isInverted && (
                                            <span className="tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-green-100 tw-text-green-700">
                                                <i className="fa fa-retweet tw-me-1" />
                                                Inverted
                                            </span>
                                        )}
                                        {isAuxiliary && (
                                            <span className="tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full tw-bg-green-100 tw-text-green-700">
                                                <i className="fa fa-layer-group tw-me-1" />
                                                Auxiliary
                                            </span>
                                        )}
                                        {signalTypeInfo.GPIO && (
                                            <span className="tw-px-3 tw-py-1 tw-bg-gray-100 tw-text-gray-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                                GPIO Bus
                                            </span>
                                        )}
                                        {signalTypeInfo.UART && (
                                            <span className="tw-px-3 tw-py-1 tw-bg-indigo-100 tw-text-indigo-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                                UART Bus
                                            </span>
                                        )}
                                        {signalTypeInfo.I2C && (
                                            <span className="tw-px-3 tw-py-1 tw-bg-teal-100 tw-text-teal-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                                I2C Bus
                                            </span>
                                        )}
                                        {signalTypeInfo.VIRTUAL && (
                                            <span className="tw-px-3 tw-py-1 tw-bg-slate-100 tw-text-slate-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                                Virtual
                                            </span>
                                        )}
                                        {busLabel && (
                                            <span
                                                className={`tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-rounded-full ${
                                                    bindingProtocol === "i2c"
                                                        ? "tw-bg-teal-100 tw-text-teal-700"
                                                        : "tw-bg-indigo-100 tw-text-indigo-700"
                                                }`}
                                            >
                                                {busLabel}
                                            </span>
                                        )}
                                    </div>
                                    {(addressChip || (status !== undefined && status !== 0)) && (
                                        <div className="tw-flex tw-flex-wrap tw-gap-2">
                                            {addressChip}
                                            {status !== undefined && status !== 0 && (
                                                <span className="tw-px-3 tw-py-1 tw-bg-red-100 tw-text-red-700 tw-text-xs tw-font-semibold tw-rounded-full">
                                                    <i className="fa fa-triangle-exclamation tw-me-1" />
                                                    {STATUS_LABELS[status] || `Status ${status}`}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Right side - Signal count */}
                        {isExpanded && totalSignals > 0 && (
                            <div className="tw-flex tw-items-center tw-text-sm tw-text-gray-500 tw-whitespace-nowrap">
                                {currentSignalIndex + 1} of {totalSignals} signals
                            </div>
                        )}
                    </div>

                    <button
                        className={`tw-rounded-full tw-text-white tw-transition-colors ${
                            isExpanded ? "tw-p-2" : "tw-p-1"
                        } ${
                            currentSignalIndex >= totalSignals - 1
                                ? "tw-bg-gray-300 tw-cursor-not-allowed"
                                : "tw-bg-blue-500 hover:tw-bg-blue-600"
                        }`}
                        onClick={onNextSignal}
                        disabled={currentSignalIndex >= totalSignals - 1}
                    >
                        <ChevronRight
                            className={`${
                                isExpanded ? "tw-h-6 tw-w-6" : "tw-h-4 tw-w-4"
                            }`}
                        />
                    </button>
                </div>
            </div>
        );
    }
);

DeviceMonitorControl.displayName = "DeviceMonitorControl";

export default DeviceMonitorControl;
